import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Targeting } from 'src/app/classes/targeting/targeting';
import { SpAudienceScoreComponent } from '../../../sp-audience-score/sp-audience-score.component';
import { TargetingUtils } from 'src/app/utils/targeting/targeting-utils';

@Component({
  selector: 'app-audience-preview',
  templateUrl: './audience-preview.component.html',
  styleUrls: ['./audience-preview.component.scss'],
  standalone: true,
  imports: [CommonModule, SpAudienceScoreComponent]
})
export class AudiencePreviewComponent {
  constructor() {}
  @Input() audience: Targeting | null = null;
  @Input() audienceSize: number | null = null;
  @Input() audienceScore: string = 'size_good';
  @Input() onApply?: () => void;

  get genderText(): string {
    if (!this.audience || !this.audience.genders) {
      return 'All';
    }
    if (this.audience.genders.length === 2) {
      return 'All';
    }
    if (this.audience.genders.length === 1) {
      return this.audience.genders[0] === 1 ? 'Male' : 'Female';
    }
    return 'All';
  }

  get flexibleSpecGroups(): Array<{interests: string[], behaviors: string[], occupations: string[]}> {
    if (!this.audience?.flex) return [];

    const groups: Array<{interests: string[], behaviors: string[], occupations: string[]}> = [];

    this.audience.flex.forEach(flexSpec => {
      const group = {
        interests: [] as string[],
        behaviors: [] as string[],
        occupations: [] as string[]
      };

      // Extract interests
      const interests = flexSpec.get('interests');
      if (interests && interests.length > 0) {
        interests.forEach(entry => {
          if (entry instanceof Map) {
            group.interests.push(entry.get('name') || `Interest ${entry.get('id')}`);
          }
        });
      }

      // Extract behaviors
      const behaviorKeys = ['behaviors', 'life_events'];
      behaviorKeys.forEach(key => {
        const values = flexSpec.get(key);
        if (values && values.length > 0) {
          values.forEach(entry => {
            if (entry instanceof Map) {
              group.behaviors.push(entry.get('name') || `${key} ${entry.get('id')}`);
            }
          });
        }
      });

      // Extract occupations
      const occupationKeys = ['work_employers', 'work_positions', 'education_statuses', 'family_statuses'];
      occupationKeys.forEach(key => {
        const values = flexSpec.get(key);
        if (values && values.length > 0) {
          values.forEach(entry => {
            if (entry instanceof Map) {
              group.occupations.push(entry.get('name') || `${key} ${entry.get('id')}`);
            }
          });
        }
      });

      // Only add the group if it has at least one item
      if (group.interests.length > 0 || group.behaviors.length > 0 || group.occupations.length > 0) {
        groups.push(group);
      }
    });

    return groups;
  }

  get locations(): string[] {
    if (!this.audience?.geolocations) return [];

    const locations: string[] = [];
    const geo = this.audience.geolocations;

    // Country groups
    if (geo.country_groups && geo.country_groups.length > 0) {
      geo.country_groups.forEach(group => {
        const displayName = group === 'worldwide' ? 'Worldwide' : group.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        locations.push(`Country Group: ${displayName}`);
      });
    }

    // Countries
    if (geo.countries && geo.countries.length > 0) {
      geo.countries.forEach(country => {
        locations.push(`Country: ${country}`);
      });
    }

    // Regions
    if (geo.regions?.length > 0) {
      geo.regions.forEach(region => locations.push(`Region: ${region.name || region.key}`));
    }

    // Medium geo areas
    if (geo.medium_geo_areas?.length > 0) {
      geo.medium_geo_areas.forEach(area => locations.push(`Area: ${area.name || area.key}`));
    }

    // Cities
    if (geo.cities?.length > 0) {
      geo.cities.forEach(city => locations.push(`City: ${city.name || city.key}`));
    }

    // Zip codes
    if (geo.zips?.length > 0) {
      geo.zips.forEach(zip => locations.push(`Zip: ${zip.name || zip.key}`));
    }

    // Subcities
    if (geo.subcities?.length > 0) {
      geo.subcities.forEach(subcity => locations.push(`Subcity: ${subcity.name || subcity.key}`));
    }

    // Neighborhoods
    if (geo.neighborhoods?.length > 0) {
      geo.neighborhoods.forEach(neighborhood => locations.push(`Neighborhood: ${neighborhood.name || neighborhood.key}`));
    }

    return locations;
  }

  get localeLabels(): string[] {
    if (!this.audience?.locales || this.audience.locales.length === 0) return [];

    return this.audience.locales.map(localeId =>
      TargetingUtils.getLabelForType('locales', localeId)
    );
  }

  get relationshipStatusLabels(): string[] {
    if (!this.audience?.relationshipStatuses || this.audience.relationshipStatuses.length === 0) return [];

    return this.audience.relationshipStatuses.map(statusId =>
      TargetingUtils.getLabelForType('relationship_statuses', statusId)
    );
  }

  handleApply(): void {
    if (this.onApply) {
      this.onApply();
    }
  }
}
